import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import {
  FIELD_INCORRECTLY,
  INN_ERROR,
  KPP_ERROR,
  MAX_CHARACTERS_ERROR,
  MIN_CHARACTERS_ERROR,
  OGRN_LENGTH_ERROR,
  RAFP_NZA_ERROR,
  REQUIRED_ERROR,
  UNALLOWED_CHARACTERS_ERROR,
} from 'CorePackage/MainModule/constants/validationErrors';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeIsNormalInteger } from 'CorePackage/MainModule/validators/isNormalInteger';
import { makeIsOgrn } from 'CorePackage/MainModule/validators/isOgrn';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { makeIsErknmInn } from 'CorePackage/MainModule/validators/makeIsErknmInn';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { makeSnilsValidation } from 'CorePackage/MainModule/validators/isValidSnils';
import { FormErrors } from 'KnmPackage/MainModule/types/FormErrors';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { ICatalog } from 'CorePackage/CatalogsModule/interfaces/ICatalog';
import { IDictionaryKnmKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnmKindSelectOption';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';

import { IKnmErknmFormSubject } from '../interfaces/IKnmErknmFormSubject';
import { isDigitsCountCorrect } from '../../../CorePackage/MainModule/validators/isDigitsCountCorrect';
import { validateRafpNzaStatus } from '../../../CorePackage/MainModule/validators/validateRafpNzaStatus';
import { IKnmErknmFormOrganizationAdd } from '../interfaces/IKnmErknmFormOrganizationAdd';
import { makeMinCharactersValidator } from '../../../CorePackage/MainModule/validators/minCharacters';
import { KnmResultItemSubjectTypesEnum } from '../../ErknmResultDecisionsModule/components/ErknmResultItemSubjectInfo';
import { makeInnChecksumVerification } from '../../../CorePackage/MainModule/validators/isInnCorrect';
import { FnsCheckStatusesEnum } from '../../ErknmSubjectModule/enums/FnsCheckStatusesEnum';
import { IKnmErknmFormEvent } from '../interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormAct } from '../interfaces/IKnmErknmFormAct';
import { isErknmOrganizationNameFieldEnabledWithoutFiz } from './isErknmOrganizationNameFieldEnabledWithoutFiz';

interface IGetErknmResponsibleEntitiesOrganizationValidateProps {
  item: IKnmErknmFormSubject | IKnmErknmFormOrganizationAdd;
  isOther: boolean;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
  eventsErknm: IKnmErknmFormEvent[] | undefined;
  acts: IKnmErknmFormAct[] | undefined;
  organizations: IKnmErknmFormSubject[] | undefined;
  fnsRegistryOkvedCodes:
    | {
        [key: string]: ICatalog<string[]>;
      }
    | undefined;
  isShowCheckResultMessage: boolean;
  isKnmStatusFillInProcess: boolean | undefined;
  kindKnm: IDictionaryKnmKindSelectOption | undefined;
  isSupervisor: boolean | undefined;
  isNewKnm: boolean | undefined;
  isKnmStatusReadyForApprove: boolean | undefined;
  isKnmPlanStatusConsidered: boolean;
  knmDataApprovedType: ApprovedTypesEnum | null | undefined;
  knmDataNote: string | null | undefined;
  isWatchOnly: boolean | undefined;
}

export const getErknmResponsibleEntitiesOrganizationValidate = ({
  acts,
  eventsErknm,
  fnsRegistryOkvedCodes,
  isOther,
  isShowCheckResultMessage,
  item,
  organizations,
  subjectTypeOptions,
  isKnmStatusFillInProcess,
  kindKnm,
  isKnmPlanStatusConsidered,
  isKnmStatusReadyForApprove,
  isNewKnm,
  isSupervisor,
  knmDataApprovedType,
  knmDataNote,
  isWatchOnly,
}: IGetErknmResponsibleEntitiesOrganizationValidateProps): FormErrors<IKnmErknmFormSubject> => {
  let subjectTypeCode = item.subjectType?.code;

  if (!subjectTypeCode) {
    subjectTypeCode = subjectTypeOptions?.find(i => i.title === item.subjectType?.title)?.code;
  }

  const isFIZ = subjectTypeCode === KnmResultItemSubjectTypesEnum.FIZ;
  const isFIUR = subjectTypeCode === KnmResultItemSubjectTypesEnum.FIUR;
  const isIUR = subjectTypeCode === KnmResultItemSubjectTypesEnum.IUR;
  const isIFIZ = subjectTypeCode === KnmResultItemSubjectTypesEnum.IFIZ;
  const isOFFICIAL = subjectTypeCode === KnmResultItemSubjectTypesEnum.OFFICIAL;

  const hasAction = !!eventsErknm?.find(i => i.interactionCategory?.id === 3 || i.interactionCategory?.id === 2);
  const options: (IKnmErknmFormOrganizationAdd | IKnmErknmFormSubject)[] = [];

  // Определяем список доступных КЛ для выбора, если текущее лицо, новое
  if (isOther) {
    // Определяем, откуда брать список КЛ.
    // Если есть хотябы одно действие с кодом, отличным от 1, то из актов, иначе из всех КЛ

    if (hasAction) {
      acts?.forEach(act => {
        if (act.organization && !options.find(i => i.guid === act.organization?.guid)) {
          options.push(act.organization);
        }
      });
    } else {
      organizations?.forEach(subject => {
        options.push(subject);
      });
    }
  }

  let isErrorInn: boolean = false;
  let isErrorOgrn: boolean = false;
  let isErrorSnils: boolean = false;
  let isErrorRafpNza: boolean = false;

  if (isOther) {
    isErrorInn = !!options.find(i => item.inn && i.inn === item.inn);
    isErrorOgrn = !!options.find(i => item.ogrn && i.ogrn === item.ogrn);
    isErrorSnils = !!options.find(i => item.snils && i.snils === item.snils);
    isErrorRafpNza = !!options.find(i => item.rafpNza && i.rafpNza === item.rafpNza);
  }

  const subjectFnsRegistryOkvedCodes: string[] | undefined =
    fnsRegistryOkvedCodes && item.inn ? fnsRegistryOkvedCodes[item.inn]?.data : [];

  // WORK-8361 Блокируем ОКВЭД только там, где его можно отредактировать.
  const isCheckOkved =
    isShowCheckResultMessage &&
    (isKnmStatusFillInProcess || isKnmStatusReadyForApprove) &&
    (item.checkSubjectResult?.egrip?.status === FnsCheckStatusesEnum.FOUND ||
      item.checkSubjectResult?.egrul?.status === FnsCheckStatusesEnum.FOUND);

  return {
    subjectType: composeValidators(makeRequired(REQUIRED_ERROR))(item.subjectType),
    position: isOFFICIAL ? makeRequired(REQUIRED_ERROR)(item.position) : undefined,
    ogrn:
      item.subjectType && !item.isFiz
        ? composeValidators(
            makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR),
            makeMaxCharactersValidator(32, MAX_CHARACTERS_ERROR),
            makeIsOgrn(OGRN_LENGTH_ERROR),
            isErrorOgrn ? () => 'Контролируемое лицо уже присутствует в паспорте КНМ' : () => undefined,
          )(item.ogrn)
        : undefined,
    inn: composeValidators(
      !item.rafpNza && !isIUR && !isIFIZ && !item.snils ? makeRequired(REQUIRED_ERROR) : () => undefined,
      item.inn ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
      item.inn
        ? makeIsErknmInn({
            errorMessage: INN_ERROR(),
            getIsFiz: () => item.isFiz || isOFFICIAL,
            getIsRequired: () => kindKnm?.code !== KnmKindCodesEnum.RO,
          })
        : () => undefined,
      item.inn ? makeInnChecksumVerification() : () => undefined,
      isErrorInn ? () => 'Контролируемое лицо уже присутствует в паспорте КНМ' : () => undefined,
    )(item.inn),
    rafpNza: composeValidators(
      isFIUR && !item.inn && !item.kpp ? makeRequired(REQUIRED_ERROR) : () => undefined,
      isFIUR && !item.inn && !item.kpp ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
      isFIUR && !item.inn && !item.kpp
        ? isDigitsCountCorrect({
            errorMessage: RAFP_NZA_ERROR,
            digitsCount: 11,
          })
        : () => undefined,
      isFIUR && !item.inn && !item.kpp ? validateRafpNzaStatus() : () => undefined,
      isErrorRafpNza ? () => 'Контролируемое лицо уже присутствует в паспорте КНМ' : () => undefined,
    )(item.rafpNza),
    kpp: composeValidators(
      isFIUR && !item.rafpNza ? makeRequired(REQUIRED_ERROR) : () => undefined,
      isFIUR && !item.rafpNza ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
      isFIUR && !item.rafpNza
        ? isDigitsCountCorrect({
            errorMessage: KPP_ERROR,
            digitsCount: 9,
          })
        : () => undefined,
    )(item.kpp),
    organizationName: composeValidators(
      makeRequired(
        REQUIRED_ERROR,
        () =>
          !(
            isErknmOrganizationNameFieldEnabledWithoutFiz({
              itemIzFiz: isFIZ || isOFFICIAL,
              isKnmStatusFillInProcess,
              isWatchOnly,
              isSupervisor,
              isNewKnm,
              isKnmStatusReadyForApprove,
              isKnmPlanStatusConsidered,
              knmDataApprovedType,
              knmDataNote,
            }) &&
            (isFIZ || isOFFICIAL)
          ) && !isIFIZ,
      ),
      makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
    )(item.organizationName),
    okveds: item.okveds?.map(okved => ({
      okved: composeValidators(
        makeRequired('Поле не может быть пустым'),
        okved.okved && isCheckOkved && !subjectFnsRegistryOkvedCodes?.includes(okved.okved.code)
          ? () => FIELD_INCORRECTLY
          : () => undefined,
      )(okved.okved),
    })),
    snils: composeValidators(
      !item.inn && (isFIZ || isOFFICIAL) ? makeRequired(REQUIRED_ERROR) : () => undefined,
      makeSnilsValidation('Поле заполнено некорректно', () => !item.snils),
      isErrorSnils ? () => 'Контролируемое лицо уже присутствует в паспорте КНМ' : () => undefined,
    )(item.snils),
    country: makeRequired(REQUIRED_ERROR, () => !isIUR && !isIFIZ)(item.country),
    document: composeValidators(
      makeRequired(REQUIRED_ERROR, () => !isIFIZ),
      makeMinCharactersValidator(3, MIN_CHARACTERS_ERROR(3), () => !isIFIZ),
      makeMaxCharactersValidator(1000, MAX_CHARACTERS_ERROR, () => !isIFIZ),
    )(item.document),
  };
};
